import { inject, Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular/standalone';

export interface PromptField {
  name: string;
  type: 'text' | 'number';
  placeholder: string;
  value?: string | number;
}

export interface ConfirmOptions {
  /** A destructive action: the confirm button turns red. Name the action in `confirmText` ("Delete user"). */
  danger?: boolean;
}

/** Promise-based yes/no confirm — replaces the @curbnturf/alert facade for admin actions. */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly alerts = inject(AlertController);

  async confirm(header: string, message = '', confirmText = 'Yes', options: ConfirmOptions = {}): Promise<boolean> {
    const alert = await this.alerts.create({
      header,
      message,
      cssClass: options.danger ? 'admin-alert-danger' : undefined,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: confirmText, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  /** Confirm with input fields; resolves to the entered values, or undefined if cancelled. */
  async prompt(
    header: string,
    message: string,
    inputs: PromptField[],
    confirmText = 'Confirm',
  ): Promise<Record<string, string> | undefined> {
    const alert = await this.alerts.create({
      header,
      message,
      inputs,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: confirmText, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role, data } = await alert.onDidDismiss<{ values: Record<string, string> }>();
    return role === 'confirm' ? data?.values ?? {} : undefined;
  }
}
